// ─── 🌙 Cassa → Caparre (fase 3, 26/09/2026) ────────────────────────────────
//
// Dal mockup approvato: per un periodo, le caparre delle partite di quei giorni —
// incassate, scalate, trattenute, nel borsellino —, le esentate col motivo, le
// trattenute ai maestri, e i pagamenti di SumUp che non corrispondono a nessuna
// caparra. Si scarica in CSV per il commercialista.
//
// ⛔ Nessun totale arriva dal server: le tessere si sommano qui, dalle righe che
// si vedono sotto, così un numero che non torna si vede subito.

import { useMemo, useState } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import {
  comeEFinita,
  ETICHETTE_CAPARRA,
  formatCents,
  type ResocontoCaparre,
  type RigaResoconto,
  type SumupDaAbbinare,
} from '@pmo/shared';
import { chiama, scarica } from '../../lib/api.js';
import { Attesa, Guasto } from '../../components/pezzi.js';
import { oggi } from '../../lib/time.js';
import { CampoData } from '../../components/CampoData.js';

const COME: Record<string, string> = {
  card: 'SumUp online',
  credit: 'Crediti',
  mixed: 'Crediti + SumUp',
  desk_cash: 'banco, contanti',
  desk_card: 'banco, carta',
};

/** Il colore dello stato, lo stesso della scheda. */
const PILLOLA: Record<RigaResoconto['status'], string> = {
  pending: 'pill-attesa',
  paid: 'pill-ok',
  consumed: 'pill-ok',
  forfeited: 'pill-persa',
  credited: 'pill-credito',
  expired: 'pill-credito',
};

type Filtro = 'tutte' | 'trattenute' | 'borsellino' | 'attesa' | 'sumup';

const primoDelMese = (g: string) => `${g.slice(0, 8)}01`;

export function Caparre(): React.JSX.Element {
  const [dal, setDal] = useState(() => primoDelMese(oggi()));
  const [al, setAl] = useState(() => oggi());
  const [filtro, setFiltro] = useState<Filtro>('tutte');
  const periodo = `from=${dal}&to=${al}`;
  const r = useQuery({
    queryKey: ['resoconto-caparre', dal, al],
    queryFn: () => chiama<ResocontoCaparre>('GET', `/deposits/report?${periodo}`),
    enabled: dal <= al,
  });
  const sumup = useQuery({
    queryKey: ['sumup-da-abbinare', dal, al],
    queryFn: () => chiama<SumupDaAbbinare>('GET', `/deposits/report/sumup?${periodo}`),
    enabled: filtro === 'sumup' && dal <= al,
  });
  const csv = useMutation({
    meta: { fatto: 'resoconto degli acconti scaricato' },
    mutationFn: () => scarica(`/deposits/report.csv?${periodo}`, `acconti-${dal}-${al}.csv`),
  });

  const t = useMemo(() => tessere(r.data?.rows ?? []), [r.data]);
  const righe = (r.data?.rows ?? []).filter((x) =>
    filtro === 'trattenute'
      ? x.status === 'forfeited'
      : filtro === 'borsellino'
        ? x.status === 'credited'
        : filtro === 'attesa'
          ? x.status === 'pending'
          : true,
  );

  return (
    <div className="resoconto-caparre">
      <div className="riga-campi">
        <CampoData etichetta="Partite dal" value={dal} onChange={setDal} />
        <CampoData etichetta="al" value={al} onChange={setAl} />
        <button
          type="button"
          className="bordato"
          disabled={csv.isPending || dal > al}
          onClick={() => csv.mutate()}
        >
          ⬇ Scarica CSV
        </button>
      </div>
      {dal > al && <p className="avvertenza">La data d’inizio viene dopo quella di fine.</p>}
      {r.isLoading && <Attesa cosa="gli acconti" />}
      <Guasto errore={r.error ?? csv.error} />

      {r.data && (
        <>
          <div className="tessere-caparre">
            <Tessera
              titolo="Incassati"
              cifra={formatCents(t.incassate)}
              sotto={`${t.quanteIncassate} acconti · SumUp ${formatCents(t.sumup)} · banco ${formatCents(t.banco)} · Crediti ${formatCents(t.credito)}`}
            />
            <Tessera
              titolo="Scalati dalle partite"
              cifra={formatCents(t.scalate)}
              sotto={`${t.quanteScalate} partite giocate`}
            />
            <Tessera
              titolo="Trattenuti"
              cifra={formatCents(t.trattenute)}
              sotto={`${t.tardive} disdette tardive · ${t.noShow} no-show`}
            />
            <Tessera
              titolo="Nel borsellino"
              cifra={formatCents(t.borsellino)}
              sotto="disdette in tempo, partite annullate dal circolo e organizzatori cambiati, come Crediti"
            />
            <Tessera
              titolo="Trattenute ai maestri"
              cifra={formatCents(r.data.deductions.reduce((s, x) => s + x.amountCents, 0))}
              sotto={`${r.data.deductions.length} lezioni`}
            />
            <Tessera
              titolo="Esentate"
              cifra={String(r.data.exemptions.length)}
              sotto="ognuna col suo motivo"
            />
          </div>

          <div className="linguette-filtro" role="group" aria-label="Quali righe">
            {(
              [
                ['tutte', 'Tutti'],
                ['trattenute', 'Trattenuti'],
                ['borsellino', 'Nel borsellino'],
                ['attesa', 'In attesa'],
                ['sumup', 'SumUp da abbinare'],
              ] as const
            ).map(([v, e]) => (
              <button
                key={v}
                type="button"
                className={filtro === v ? undefined : 'bordato'}
                aria-pressed={filtro === v}
                onClick={() => setFiltro(v)}
              >
                {e}
              </button>
            ))}
          </div>

          {filtro === 'sumup' ? (
            <SumupDaAbbinareElenco dati={sumup.data} errore={sumup.error} carica={sumup.isLoading} />
          ) : (
            <div className="tabella-scorre">
              <table className="tabella">
                <thead>
                  <tr>
                    <th>Partita</th>
                    <th>Organizzatore</th>
                    <th>Pagato</th>
                    <th>Com’è finita</th>
                    <th className="a-destra">€</th>
                  </tr>
                </thead>
                <tbody>
                  {righe.length === 0 && (
                    <tr>
                      <td colSpan={5} className="tenue">
                        Nessun acconto in questo periodo.
                      </td>
                    </tr>
                  )}
                  {righe.map((x) => (
                    <tr key={x.depositId}>
                      <td>
                        {x.date.slice(8, 10)}/{x.date.slice(5, 7)} · {x.time} · {x.courtName}
                        {x.series && <span className="tenue piccolo"> · campo fisso</span>}
                      </td>
                      <td>{x.memberName}</td>
                      <td>
                        {x.paidWith ? COME[x.paidWith] : '—'}
                        {x.transactionCode && <span className="tenue piccolo"> · {x.transactionCode}</span>}
                      </td>
                      <td>
                        <span className={`pill-caparra ${PILLOLA[x.status]}`}>
                          {/* «Caparra» da sola, in un elenco di caparre, non dice niente. */}
                          {x.status === 'paid' ? 'Pagato' : ETICHETTE_CAPARRA[x.status]}
                        </span>{' '}
                        {x.status === 'paid' ? 'da scalare' : comeEFinita(x.closeReason)}
                      </td>
                      <td className="a-destra">{formatCents(x.amountCents)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {filtro === 'tutte' && r.data.exemptions.length > 0 && (
            <div className="riquadro">
              <h3>Esentate</h3>
              <ul>
                {r.data.exemptions.map((e, i) => (
                  <li key={i}>
                    {e.date
                      ? `${e.date.slice(8, 10)}/${e.date.slice(5, 7)} · ${e.time} · ${e.courtName ?? ''}`
                      : ''}{' '}
                    · {e.what}: <em>{e.reason}</em>
                    {e.by && <span className="tenue piccolo"> · {e.by}</span>}
                  </li>
                ))}
              </ul>
            </div>
          )}
          {filtro === 'tutte' && r.data.deductions.length > 0 && (
            <div className="riquadro">
              <h3>Trattenute ai maestri</h3>
              <ul>
                {r.data.deductions.map((d) => (
                  <li key={d.id}>
                    <strong>{d.instructorName}</strong> · {d.date.slice(8, 10)}/{d.date.slice(5, 7)} alle{' '}
                    {d.time} ·{' '}
                    {d.reason === 'no_show'
                      ? 'l’allievo non si è presentato'
                      : `disdetta ${d.hoursBefore ?? 0} ore prima`}{' '}
                    · <strong>− {formatCents(d.amountCents)}</strong>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </>
      )}
    </div>
  );
}

function Tessera({
  titolo,
  cifra,
  sotto,
}: {
  titolo: string;
  cifra: string;
  sotto: string;
}): React.JSX.Element {
  return (
    <div className="tessera-caparre">
      <span className="tenue piccolo">{titolo}</span>
      <strong>{cifra}</strong>
      <span className="tenue piccolo">{sotto}</span>
    </div>
  );
}

function SumupDaAbbinareElenco({
  dati,
  errore,
  carica,
}: {
  dati: SumupDaAbbinare | undefined;
  errore: unknown;
  carica: boolean;
}): React.JSX.Element {
  if (carica) return <Attesa cosa="i pagamenti di SumUp" />;
  if (errore) return <Guasto errore={errore} />;
  if (!dati) return <></>;
  if (dati.unavailable) return <p className="avvertenza">{dati.unavailable}</p>;
  if (dati.items.length === 0) {
    return (
      <p className="tenue">
        ✓ Ogni pagamento di SumUp del periodo corrisponde a un acconto: niente di sospeso.
      </p>
    );
  }
  return (
    <>
      <p className="piccolo">
        Pagamenti di SumUp con l’importo dell’acconto che non corrispondono a nessun acconto. Controlla nella
        scheda della partita, o nel pannello di SumUp.
      </p>
      <ul>
        {dati.items.map((x) => (
          <li key={x.transactionCode}>
            {new Date(x.at).toLocaleString('it-IT', {
              day: 'numeric',
              month: 'short',
              hour: '2-digit',
              minute: '2-digit',
            })}{' '}
            · {formatCents(x.amountCents)} · codice {x.transactionCode}
            {x.paymentType && <span className="tenue piccolo"> · {x.paymentType.toLowerCase()}</span>}
          </li>
        ))}
      </ul>
    </>
  );
}

function tessere(righe: readonly RigaResoconto[]) {
  const t = {
    incassate: 0,
    quanteIncassate: 0,
    sumup: 0,
    banco: 0,
    credito: 0,
    scalate: 0,
    quanteScalate: 0,
    trattenute: 0,
    tardive: 0,
    noShow: 0,
    borsellino: 0,
  };
  for (const x of righe) {
    const pagata = x.paidWith !== null && x.status !== 'pending' && !(x.status === 'expired');
    if (pagata) {
      t.incassate += x.amountCents;
      t.quanteIncassate += 1;
      t.credito += x.creditCents;
      if (x.paidWith === 'desk_cash' || x.paidWith === 'desk_card') t.banco += x.cardCents;
      else t.sumup += x.cardCents;
    }
    if (x.status === 'consumed') {
      t.scalate += x.amountCents;
      t.quanteScalate += 1;
    }
    if (x.status === 'forfeited') {
      t.trattenute += x.amountCents;
      if (x.closeReason === 'no_show') t.noShow += 1;
      else t.tardive += 1;
    }
    if (x.status === 'credited') t.borsellino += x.amountCents;
  }
  return t;
}
